import { Printer, RefreshCw, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui';
import { loadEmergency, refreshEmergency } from '@/lib/emergency';
import { date, money } from '@/lib/format';

/** Works without a connection: what this computer last saved from the PMS. */
export function EmergencyPage() {
  const [saved, setSaved] = useState(loadEmergency);
  const [busy, setBusy] = useState(false);
  if (!saved) {
    return (
      <div className="mx-auto max-w-xl p-8 text-center">
        <TriangleAlert className="mx-auto size-10 text-amber-500" />
        <h1 className="mt-3 text-xl font-semibold">No emergency list on this computer</h1>
        <p className="mt-2 text-sm text-slate-600">Front-desk computers keep a copy while a front-desk user is signed in. It is removed at sign-out and after 24 hours.</p>
        <Link to="/login" className="mt-4 inline-block text-sm text-brand-700 underline">
          Sign in
        </Link>
      </div>
    );
  }
  const { data, savedAt } = saved;
  const ageMin = Math.round((Date.now() - savedAt) / 60_000);
  const time = (iso: number | string) => new Intl.DateTimeFormat('en-GB', { timeZone: 'Africa/Lagos', hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }).format(new Date(iso));
  const occupied = new Set(data.inHouse.map((s) => s.room));
  const byFloor = new Map<number, typeof data.rooms>();
  for (const r of data.rooms) byFloor.set(r.floor, [...(byFloor.get(r.floor) ?? []), r]);
  return (
    <div className="mx-auto max-w-5xl p-4 text-[13px] print:p-0">
      <div className={`mb-4 flex flex-wrap items-center gap-3 rounded-lg px-3 py-2 print:hidden ${ageMin > 30 ? 'bg-rose-50 text-rose-900 ring-1 ring-rose-200' : 'bg-amber-50 text-amber-900 ring-1 ring-amber-200'}`}>
        <TriangleAlert className="size-5" />
        <span>
          Emergency copy saved on this computer at <b>{time(savedAt)}</b> ({ageMin} min ago). Changes made since then are not shown.
        </span>
        <div className="ml-auto flex gap-2">
          <Button
            size="sm"
            loading={busy}
            icon={<RefreshCw className="size-3.5" />}
            onClick={async () => {
              setBusy(true);
              if (await refreshEmergency()) setSaved(loadEmergency());
              setBusy(false);
            }}
          >
            Try to refresh
          </Button>
          <Button size="sm" icon={<Printer className="size-3.5" />} onClick={() => window.print()}>
            Print
          </Button>
        </div>
      </div>
      <h1 className="text-lg font-semibold">
        {data.hotel} — emergency list, business date {date(data.businessDate, 'long')}
      </h1>
      <p className="text-slate-600">
        In house: <b>{data.totals.rooms}</b> rooms · <b>{data.totals.adults}</b> adults · <b>{data.totals.children}</b> children · printed from the copy of {time(data.generatedAt)}
      </p>

      <h2 className="mt-4 font-semibold">In house (by room)</h2>
      <table className="table-dense mt-1 w-full">
        <thead>
          <tr>
            <th>Room</th>
            <th>Guest</th>
            <th>Pax</th>
            <th>Departs</th>
            <th>Company</th>
            <th className="text-right">Balance</th>
            <th className="w-16">Seen ✓</th>
          </tr>
        </thead>
        <tbody>
          {data.inHouse.map((s) => (
            <tr key={s.confirmationNo}>
              <td className="num font-semibold">{s.room}</td>
              <td>
                {s.guestName} {s.vipCode && <b>({s.vipCode})</b>}
              </td>
              <td className="num">
                {s.adults}
                {s.children ? `+${s.children}` : ''}
              </td>
              <td className="num">{date(s.departureDate)}</td>
              <td>{s.company ?? ''}</td>
              <td className="num text-right">{money(s.balance)}</td>
              <td />
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div>
          <h2 className="font-semibold">Arrivals today ({data.arrivals.length})</h2>
          <ul className="mt-1 space-y-0.5">
            {data.arrivals.map((s) => (
              <li key={s.confirmationNo}>
                {s.guestName} · {s.roomType}
                {s.room ? ` · room ${s.room}` : ''}
                {s.eta ? ` · ETA ${s.eta.slice(0, 5)}` : ''} · {s.adults} pax
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="font-semibold">Departures today ({data.departures.length})</h2>
          <ul className="mt-1 space-y-0.5">
            {data.departures.map((s) => (
              <li key={s.confirmationNo}>
                Room {s.room} · {s.guestName} · balance {money(s.balance)}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <h2 className="mt-4 font-semibold">Rooms</h2>
      {[...byFloor.entries()].map(([floor, rooms]) => (
        <div key={floor} className="mt-1 flex flex-wrap gap-1">
          {rooms.map((r) => (
            <span key={r.number} className={`num rounded px-1.5 py-0.5 ring-1 ${occupied.has(r.number) ? 'bg-sky-50 ring-sky-300' : 'ring-slate-200'}`}>
              {r.number} <span className="text-[10px] text-slate-500">{r.hkStatus.toLowerCase()}</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}
